import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../api";
import { Spinner } from "../../components/Logo";
import { PageHeader, StatusChip } from "../../components/ui";
import { Search, Ban, CheckCircle2, ShieldPlus, KeyRound, ShieldOff } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    const params = search ? { search } : {};
    api.get("/admin/users", { params }).then((r) => setUsers(r.data.users)).catch(() => {});
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const suspend = (u) => act(() => api.post(`/admin/users/${u.id}/suspend`, { reason: "Admin action" }), "User suspended");
  const reactivate = (u) => act(() => api.post(`/admin/users/${u.id}/reactivate`), "User reactivated");
  const setRole = (u, role) => act(() => api.post(`/admin/users/${u.id}/role`, { role }), "Role updated");
  const resetPw = (u) => {
    const pw = window.prompt(`New password for ${u.email}:`);
    if (pw) act(() => api.post(`/admin/users/${u.id}/reset-password`, { password: pw }), "Password reset");
  };

  return (
    <div data-testid="admin-users">
      <PageHeader overline="Users" title="User management" desc="Search, suspend, reactivate, reset passwords and manage roles." />
      <div className="relative mb-4 max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input className="input pl-10" placeholder="Search by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="user-search" />
      </div>

      {!users ? <Spinner /> : (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-slate-400 uppercase text-xs tracking-wider border-b border-white/10">
                <th className="py-3 px-4">User</th><th className="px-4">Role</th><th className="px-4">Status</th><th className="px-4">XP</th><th className="px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4">
                    <div className="font-medium">{u.full_name}</div>
                    <div className="text-slate-500 text-xs">{u.email}</div>
                  </td>
                  <td className="px-4">
                    <span className={`chip ${u.role === "super_admin" ? "bg-[#F26522]/15 text-[#F26522]" : "bg-white/5 text-slate-300"}`}>
                      {u.role === "super_admin" ? "Super Admin" : "User"}
                    </span>
                  </td>
                  <td className="px-4"><StatusChip status={u.status} /></td>
                  <td className="px-4 font-mono text-slate-300">{u.xp}</td>
                  <td className="px-4">
                    <div className="flex items-center justify-end gap-1">
                      {u.status === "active" ? (
                        <button className="btn-ghost !px-2 text-red-400" title="Suspend" onClick={() => suspend(u)} data-testid={`suspend-${u.id}`}><Ban size={16} /></button>
                      ) : (
                        <button className="btn-ghost !px-2 text-emerald-400" title="Reactivate" onClick={() => reactivate(u)} data-testid={`reactivate-${u.id}`}><CheckCircle2 size={16} /></button>
                      )}
                      {u.role === "super_admin" ? (
                        <button className="btn-ghost !px-2 text-amber-400" title="Demote to User" onClick={() => setRole(u, "user")}><ShieldOff size={16} /></button>
                      ) : (
                        <button className="btn-ghost !px-2 text-[#38bdf8]" title="Promote to Admin" onClick={() => setRole(u, "super_admin")} data-testid={`promote-${u.id}`}><ShieldPlus size={16} /></button>
                      )}
                      <button className="btn-ghost !px-2 text-slate-400" title="Reset password" onClick={() => resetPw(u)}><KeyRound size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
